/**
 * SKU Lookup Modal Component
 * ==========================
 * Modal for looking up product info by SKU using AI.
 * Shows preview of extracted data before applying to form.
 */

import { useState, useEffect, useCallback } from 'react'
import { X, Loader2, Check, AlertCircle, ExternalLink, Sparkles } from 'lucide-react'
import { skuLookupApi } from '@/services/api'
import type { SKULookupResult } from '@/types'

interface SKULookupModalProps {
  sku: string
  onApply: (data: {
    name?: string
    description?: string
    manufacturer?: string
    compatible_products?: string
    imageUrl?: string
  }) => void
  onClose: () => void
}

export default function SKULookupModal({ sku, onApply, onClose }: SKULookupModalProps) {
  const [result, setResult] = useState<SKULookupResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  // Track which fields user wants to apply
  const [applyFields, setApplyFields] = useState({
    name: true,
    description: true,
    manufacturer: true,
    compatible_products: true,
    image: true,
  })

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Perform SKU lookup when modal opens
  useEffect(() => {
    const lookupSKU = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await skuLookupApi.lookup(sku)
        setResult(response.data)
        if (response.data.error) {
          setError(response.data.error)
        } else if (!response.data.product) {
          setError('Could not extract product information from search results.')
        } else if (response.data.images.length > 0) {
          // Auto-select first image
          setSelectedImage(response.data.images[0].url)
        }
      } catch (err) {
        console.error('SKU lookup failed:', err)
        setError('Failed to look up SKU. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    lookupSKU()
  }, [sku])

  // Handle apply
  const handleApply = useCallback(() => {
    if (!result?.product) return

    const data: Parameters<typeof onApply>[0] = {}

    if (applyFields.name && result.product.name) {
      data.name = result.product.name
    }
    if (applyFields.description && result.product.description) {
      data.description = result.product.description
    }
    if (applyFields.manufacturer && result.product.manufacturer) {
      data.manufacturer = result.product.manufacturer
    }
    if (applyFields.compatible_products && result.product.compatible_products) {
      data.compatible_products = result.product.compatible_products
    }
    if (applyFields.image && selectedImage) {
      data.imageUrl = selectedImage
    }

    onApply(data)
    onClose()
  }, [result, applyFields, selectedImage, onApply, onClose])

  // Confidence badge color
  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/50 rounded-lg">
              <Sparkles size={20} className="text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                SKU Lookup Preview
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                SKU: <span className="font-mono">{sku}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Close (Esc)"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={40} className="animate-spin text-primary-500 mb-4" />
              <p className="text-gray-600 dark:text-gray-300 font-medium">Looking up product info...</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Searching the web and extracting details with AI
              </p>
            </div>
          ) : error && !result?.product ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle size={40} className="text-red-500 mb-4" />
              <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Try a different SKU or enter details manually
              </p>
            </div>
          ) : result?.product ? (
            <div className="space-y-6">
              {/* Confidence indicator */}
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${getConfidenceColor(result.product.confidence)}`}>
                  {result.product.confidence.toUpperCase()} CONFIDENCE
                </span>
                {result.product.source_url && (
                  <a
                    href={result.product.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                  >
                    View source <ExternalLink size={12} />
                  </a>
                )}
              </div>

              {/* Extracted fields */}
              <div className="space-y-4">
                {/* Name */}
                {result.product.name && (
                  <div className="p-4 border dark:border-gray-700 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Product Name
                        </label>
                        <p className="mt-1 text-gray-900 dark:text-white font-medium">
                          {result.product.name}
                        </p>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyFields.name}
                          onChange={(e) => setApplyFields(prev => ({ ...prev, name: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Apply</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Description */}
                {result.product.description && (
                  <div className="p-4 border dark:border-gray-700 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Description
                        </label>
                        <p className="mt-1 text-gray-700 dark:text-gray-300 text-sm">
                          {result.product.description}
                        </p>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyFields.description}
                          onChange={(e) => setApplyFields(prev => ({ ...prev, description: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Apply</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Manufacturer */}
                {result.product.manufacturer && (
                  <div className="p-4 border dark:border-gray-700 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Manufacturer
                        </label>
                        <p className="mt-1 text-gray-900 dark:text-white font-medium">
                          {result.product.manufacturer}
                        </p>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyFields.manufacturer}
                          onChange={(e) => setApplyFields(prev => ({ ...prev, manufacturer: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Apply</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Compatible Products */}
                {result.product.compatible_products && (
                  <div className="p-4 border dark:border-gray-700 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Compatible Products
                        </label>
                        <p className="mt-1 text-gray-700 dark:text-gray-300 text-sm whitespace-pre-wrap">
                          {result.product.compatible_products}
                        </p>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyFields.compatible_products}
                          onChange={(e) => setApplyFields(prev => ({ ...prev, compatible_products: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Apply</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Images */}
                {result.images.length > 0 && (
                  <div className="p-4 border dark:border-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Product Images ({result.images.length})
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyFields.image}
                          onChange={(e) => setApplyFields(prev => ({ ...prev, image: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Apply selected</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {result.images.map((image, index) => (
                        <div
                          key={`${image.url}-${index}`}
                          className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                            selectedImage === image.url
                              ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                          onClick={() => setSelectedImage(image.url)}
                          title={image.width && image.height ? `${image.width}×${image.height}px` : image.title}
                        >
                          <div className="aspect-square bg-gray-100 dark:bg-gray-700">
                            <img
                              src={image.thumbnail}
                              alt={image.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement
                                if (target.src !== image.url) {
                                  target.src = image.url
                                }
                              }}
                            />
                          </div>
                          {/* Image size indicator */}
                          {image.width && image.height && (
                            <div className={`absolute bottom-0 left-0 right-0 text-center py-0.5 text-[10px] font-medium ${
                              image.width >= 800 || image.height >= 800
                                ? 'bg-green-500/90 text-white'
                                : image.width >= 400 || image.height >= 400
                                  ? 'bg-yellow-500/90 text-white'
                                  : 'bg-gray-500/90 text-white'
                            }`}>
                              {image.width}×{image.height}
                            </div>
                          )}
                          {selectedImage === image.url && (
                            <div className="absolute top-1 right-1 bg-primary-500 text-white rounded-full p-0.5">
                              <Check size={12} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {result?.product && (
              <>
                {[
                  applyFields.name && result.product.name && 'name',
                  applyFields.description && result.product.description && 'description',
                  applyFields.manufacturer && result.product.manufacturer && 'manufacturer',
                  applyFields.compatible_products && result.product.compatible_products && 'compatible_products',
                  applyFields.image && selectedImage && 'image',
                ].filter(Boolean).length} fields selected
              </>
            )}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!result?.product}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                result?.product
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              <Check size={16} />
              Apply to Form
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
